import { clsx, type ClassValue } from "clsx"
import { isMobile } from "react-device-detect";
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const formatarMoeda = (valor: number | string) => {
    const numero =  new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(valor));
    return numero.replace("R$", "").trim();
};

// Função auxiliar para validar CPF
export const isValidCPF = (cpf: string): boolean => {
  if (!cpf || cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;

  let sum = 0;
  let remainder;

  // 1º dígito verificador
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(9, 10))) return false;

  // 2º dígito verificador
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.substring(10, 11))) return false;

  return true;
};

// Função auxiliar para validar phone
export const isValidPhone = (phone: string): boolean => {
    const digits = phone.replace(/\D/g, ""); // só números
    return digits.length === 11; // DDD (2) + número (9)
};

// Função auxiliar para validar Email
export const isValidEmail = (email: string): boolean => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

// ---------------------------------------------------------------------------
// CNPJ alfanumérico (Receita Federal, IN RFB nº 2.229/2024)
//
// - 14 caracteres, mesma máscara XX.XXX.XXX/XXXX-XX
// - Posições 1 a 12: alfanuméricas (letras MAIÚSCULAS A-Z e números 0-9)
// - Posições 13 e 14 (dígitos verificadores - DV): sempre numéricas
// - CNPJs antigos (100% numéricos) continuam válidos - os dois formatos convivem
// - O DV é calculado em Módulo 11, mas cada caractere vira (código ASCII - 48):
//   dígitos '0'-'9' mantêm o mesmo valor; 'A' vira 17, 'B' vira 18, e assim por diante
// ---------------------------------------------------------------------------

// Pesos do Módulo 11 para o 1º e 2º dígitos verificadores
const CNPJ_PESOS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const CNPJ_PESOS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

// Converte um caractere (0-9 ou A-Z) no valor usado no cálculo do DV
const valorCaractereCnpj = (char: string): number => char.charCodeAt(0) - 48;

// Calcula um dígito verificador a partir dos caracteres da base e dos pesos
const calcularDigitoCnpj = (chars: string[], pesos: number[]): number => {
  const soma = chars.reduce(
    (acc, char, i) => acc + valorCaractereCnpj(char) * pesos[i],
    0
  );
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
};

// Remove máscara/ruído, força maiúsculas e limita a 14 caracteres
export const unmaskCnpj = (value: string): string =>
  (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 14);

// Filtra o que pode ser digitado em cada posição: A-Z0-9 nas 12 primeiras,
// somente 0-9 nas 2 últimas (dígitos verificadores)
export const sanitizeCnpjInput = (value: string): string =>
  unmaskCnpj(value)
    .split("")
    .map((char, i) => {
      if (i >= 12) return /[0-9]/.test(char) ? char : "";
      return /[A-Z0-9]/.test(char) ? char : "";
    })
    .join("");

// Aplica a máscara XX.XXX.XXX/XXXX-XX progressivamente (aceita letras)
export const maskCnpj = (value: string): string => {
  const raw = sanitizeCnpjInput(value);
  let masked = "";
  for (let i = 0; i < raw.length; i++) {
    if (i === 2 || i === 5) masked += ".";
    if (i === 8) masked += "/";
    if (i === 12) masked += "-";
    masked += raw[i];
  }
  return masked;
};

// Função auxiliar para validar CNPJ (numérico ou alfanumérico), com ou sem máscara
export const isValidCNPJ = (cnpj: string): boolean => {
  const raw = unmaskCnpj(cnpj);

  // 12 primeiras alfanuméricas | 2 últimas numéricas
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(raw)) return false;

  // rejeita sequência de 14 caracteres iguais (ex.: "00000000000000")
  if (/^(.)\1{13}$/.test(raw)) return false;

  const base = raw.slice(0, 12).split("");
  const dv1 = calcularDigitoCnpj(base, CNPJ_PESOS_DV1);

  const baseComDv1 = [...base, String(dv1)];
  const dv2 = calcularDigitoCnpj(baseComDv1, CNPJ_PESOS_DV2);

  return raw === raw.slice(0, 12) + String(dv1) + String(dv2);
};

// Função auxiliar para validar CEP
export const isValidCEP = (cep: string): boolean => {
  const digits = cep.replace(/\D/g, "");
  return digits.length === 8;
};

// Função auxiliar para validar (genérica, só exemplo)
export const isBlankValue = (text: string): boolean => {
    return text.trim().length > 0; // apenas checa se não está vazio
};

export const HandleShare = async(share_message: string) => {
    if(isMobile){
      window.ReactNativeWebView &&
      window.ReactNativeWebView?.postMessage(share_message);
    }else {
      if (navigator.share) {
      try {
        await navigator.share({
          text: share_message,
        });
      } catch (err) {
      }
    } else {
      alert("O compartilhamento não é suportado neste navegador.");
    }
  }
}

export const isToday = (dateString:string) => {
    const [day, month, year] = dateString.split('/').map(Number);

    const inputDate = new Date(year, month - 1, day);
    const today = new Date();

    return (
      inputDate.getDate() === today.getDate() &&
      inputDate.getMonth() === today.getMonth() &&
      inputDate.getFullYear() === today.getFullYear()
    );
  }